import type { GridLayer, GridLayerOptions, Map as LeafletMap } from "leaflet";
import type { PaintRule } from "protomaps-leaflet";
import type { PrintLayerAdapter } from "./printLayerAdapterRegistry";
import type { PrintRenderContext } from "./printRenderScale";

export interface ProtomapsPrintAdapterOptions {
  map: LeafletMap;
  enabled: boolean;
  normalLayer: GridLayer | null;
  createPrintLayer: (context: PrintRenderContext) => GridLayer;
  // アンマウント後のrestoreで通常レイヤーを復活させないためのフラグ。
  isRegistrationActive?: () => boolean;
}

export const createProtomapsPrintAdapter = ({
  map,
  enabled,
  normalLayer,
  createPrintLayer,
  isRegistrationActive = () => true,
}: ProtomapsPrintAdapterOptions): PrintLayerAdapter => {
  const normalLayerWasPresent =
    normalLayer != null && map.hasLayer(normalLayer);
  let printLayer: GridLayer | null = null;

  return {
    id: "protomaps",
    apply: (context) => {
      if (
        context.printScale === 1 ||
        !enabled ||
        normalLayer == null ||
        printLayer != null ||
        !isRegistrationActive()
      ) {
        return;
      }

      if (map.hasLayer(normalLayer)) {
        map.removeLayer(normalLayer);
      }

      printLayer = createPrintLayer(context);
      const { zIndex } = normalLayer.options as GridLayerOptions;
      if (zIndex != null) {
        printLayer.setZIndex(zIndex);
      }
      printLayer.addTo(map);
    },
    restore: () => {
      const restoreErrors: unknown[] = [];
      const attemptRestore = (operation: () => void): void => {
        try {
          operation();
        } catch (error) {
          restoreErrors.push(error);
        }
      };

      const temporaryPrintLayer = printLayer;
      printLayer = null;

      if (temporaryPrintLayer != null) {
        attemptRestore(() => {
          if (map.hasLayer(temporaryPrintLayer)) {
            map.removeLayer(temporaryPrintLayer);
          }
        });
      }

      if (normalLayer != null && isRegistrationActive()) {
        attemptRestore(() => {
          if (normalLayerWasPresent && !map.hasLayer(normalLayer)) {
            normalLayer.addTo(map);
          } else if (!normalLayerWasPresent && map.hasLayer(normalLayer)) {
            map.removeLayer(normalLayer);
          }
        });
      }

      if (restoreErrors.length === 1) {
        throw restoreErrors[0];
      }
      if (restoreErrors.length > 1) {
        throw new AggregateError(
          restoreErrors,
          "Failed to restore protomaps print layers"
        );
      }
    },
  };
};

export const shiftPaintRuleZoom = <T extends PaintRule>(
  rule: T,
  zoomOffset: number
): T => ({
  ...rule,
  minzoom: rule.minzoom == null ? undefined : rule.minzoom + zoomOffset,
  maxzoom: rule.maxzoom == null ? undefined : rule.maxzoom + zoomOffset,
});
